// src/plugins/axios.js
import axios from "axios";
import constant from "../constant";
export default {
  install(Vue) {
    // Create an Axios instance
    const instance = axios.create({
      baseURL: process.env.VUE_APP_API_URL || constant.api,
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest", // Ensure this is added
      },
    });

    // Add request interceptor to inject Authorization header
    instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(constant.authToken);
        if (token) {
          // Remove quotes if token is JSON stringified
          let cleanToken = token;
          try {
            // Try to parse as JSON first (in case it's double-stringified)
            const parsed = JSON.parse(token);
            cleanToken = typeof parsed === 'string' ? parsed : token;
          } catch (e) {
            // If not valid JSON, just remove quotes manually
            if (token.startsWith('"') && token.endsWith('"')) {
              cleanToken = token.replace(/^"|"$/g, '');
            }
          }
          // Ensure token doesn't have extra whitespace
          cleanToken = cleanToken.trim();
          
          // Only set Authorization header if token is not empty
          if (cleanToken && cleanToken.length > 0) {
            config.headers["Authorization"] = `Bearer ${cleanToken}`;
            
            // Debug log in development
            if (process.env.NODE_ENV === "development") {
              console.log("Request with auth token:", {
                url: config.url,
                hasToken: !!cleanToken,
                tokenLength: cleanToken.length,
                tokenPreview: cleanToken.substring(0, 20) + "...",
                fullToken: cleanToken,
                authHeader: config.headers["Authorization"]
              });
            }
          } else {
            console.warn("Token is empty after cleaning for request:", config.url);
          }
        } else {
          console.warn("No auth token found in localStorage for request:", config.url);
        }
        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Gestion des réponses
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Si erreur 401 Unauthenticated, supprimer le token et rediriger vers login
        if (error.response && error.response.status === 401) {
          console.error("Unauthenticated error - token may be invalid or expired");
          console.error("Error details:", {
            url: error.config && error.config.url,
            message: (error.response.data && error.response.data.message) || "Unauthenticated",
            token: localStorage.getItem(constant.authToken)
          });
          
          // Supprimer le token invalide
          localStorage.removeItem(constant.authToken);
          localStorage.removeItem(constant.currentUser);
          localStorage.removeItem(constant.userRoleKey);
          
          // Rediriger vers login si on est dans le navigateur
          if (typeof window !== 'undefined' && window.location) {
            // Ne rediriger que si on n'est pas déjà sur la page de login
            if (!window.location.pathname.includes('/login')) {
              console.warn("Token invalid - redirecting to login");
              // window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );

    // ... (tes méthodes fetch, show, create, etc.)

    // Exemple d'utilisation constante pour set user role
    window.addEventListener("storage", (event) => {
      if (event.key === constant.userRoleKey) {
        store.commit("SET_USER_ROLE", event.newValue);
      }
    });

    const showResponseToast = (data) => {
      if (!data || !data.status || !data.message) {
        return;
      }

      const status = String(data.status).toLowerCase();
      if (status === "success") {
        Vue.prototype.$toast.success(data.message);
      } else if (status === "warning") {
        Vue.prototype.$toast.warning(data.message);
      } else if (status === "info") {
        Vue.prototype.$toast.info(data.message);
      } else {
        Vue.prototype.$toast.error(data.message);
      }
    };

    // Attach helper methods to Vue prototype
    Vue.prototype.$api = {
      instance, // Expose the axios instance for direct use
      async show(url) {
        Vue.prototype.$loading.start();
        try {
          const response = await instance.get(url, { timeout: 30000 });
          return response;
        } catch (error) {
          console.error("API Show Error:", error);
          const errorMessage =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "Une erreur est survenue lors de la récupération des données.";
          Vue.prototype.$toast.error(errorMessage);
          throw error;
        } finally {
          Vue.prototype.$loading.stop();
        }
      },
      async fetch(url, params = {}) {
        Vue.prototype.$loading.start();
        try {
          const response = await instance.get(url, { params, timeout: 30000 });
          return response.data;
        } catch (error) {
          console.error("API Fetch Error:", error);
          const errorMessage =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "Une erreur est survenue lors du chargement des données.";
          Vue.prototype.$toast.error(errorMessage);
          throw error;
        } finally {
          Vue.prototype.$loading.stop();
        }
      },

      // async create(url, data) {
      //   Vue.prototype.$loading.start();
      //   try {
      //     const response = await instance.post(url, data);

      //     // Show success message based on response.data.status
      //     if (response.data.status) {
      //       Vue.prototype.$toast.success(response.data.message);
      //     }

      //     return response;
      //   } catch (error) {
      //     console.error("API Create Error:", error);
      //     const errorMessage =
      //       error.response && error.response.data && error.response.data.message
      //         ? error.response.data.message
      //         : "Une erreur est survenue lors de la création.";
      //     Vue.prototype.$toast.error(errorMessage);
      //     throw error;
      //   } finally {
      //     Vue.prototype.$loading.stop(); // Uncomment this line to stop loading
      //   }
      // },

      // async update(url, data) {
      //   Vue.prototype.$loading.start();
      //   try {
      //     const response = await instance.put(url, data);
      //     if (response.data.status) {
      //       Vue.prototype.$toast.success(response.data.message);
      //     }
      //     return response;
      //   } catch (error) {
      //     console.error("API Update Error:", error);
      //     const errorMessage =
      //       error.response && error.response.data && error.response.data.message
      //         ? error.response.data.message
      //         : "Une erreur est survenue lors de la mise à jour.";
      //     Vue.prototype.$toast.error(errorMessage);
      //     throw error;
      //   } finally {
      //     Vue.prototype.$loading.stop();
      //   }
      // },
      // async delete(url) {
      //   Vue.prototype.$loading.start();
      //   try {
      //     const response = await instance.delete(url);
      //     if (response.data.status) {
      //       Vue.prototype.$toast.success(response.data.message);
      //     }
      //     return response;
      //   } catch (error) {
      //     console.error("API Delete Error:", error);
      //     const errorMessage =
      //       error.response && error.response.data && error.response.data.message
      //         ? error.response.data.message
      //         : "Une erreur est survenue lors de la suppression.";
      //     Vue.prototype.$toast.error(errorMessage);
      //     throw error;
      //   } finally {
      //     Vue.prototype.$loading.stop();
      //   }
      // },

      /*async create(url, data) {
        const confirmed = await Vue.prototype.$confirm({
          title: "Confirmation",
          message: "Voulez-vous vraiment créer cet élément ?",
          confirmButtonText: "Oui",
          cancelButtonText: "Non",
        });

        if (!confirmed) return;

        Vue.prototype.$loading.start();
        try {
          const response = await instance.post(url, data);

          showResponseToast(response.data);

          return response;
        } catch (error) {
          console.error("API Create Error:", error);
          const errorMessage =
            error.response.data.message ||
            "Une erreur est survenue lors de la création.";
          Vue.prototype.$toast.error(errorMessage);
          throw error;
        } finally {
          Vue.prototype.$loading.stop();
        }
      },

      async update(url, data) {
        const confirmed = await Vue.prototype.$confirm({
          title: "Confirmation",
          message: "Voulez-vous vraiment modifier cet élément ?",
          confirmButtonText: "Oui",
          cancelButtonText: "Non",
        });

        if (!confirmed) return;

        Vue.prototype.$loading.start();
        try {
          const response = await instance.put(url, data);

          showResponseToast(response.data);

          return response;
        } catch (error) {
          console.error("API Update Error:", error);
          const errorMessage =
            error.response.data.message ||
            "Une erreur est survenue lors de la mise à jour.";
          Vue.prototype.$toast.error(errorMessage);
          throw error;
        } finally {
          Vue.prototype.$loading.stop();
        }
      },

      async delete(url) {
        const confirmed = await Vue.prototype.$confirm({
          title: "Confirmation",
          message: "Voulez-vous vraiment supprimer cet élément ?",
          confirmButtonText: "Oui",
          cancelButtonText: "Non",
        });

        if (!confirmed) return;

        Vue.prototype.$loading.start();
        try {
          const response = await instance.delete(url);

          showResponseToast(response.data);

          return response;
        } catch (error) {
          console.error("API Delete Error:", error);
          const errorMessage =
            error.response.data.message ||
            "Une erreur est survenue lors de la suppression.";
          Vue.prototype.$toast.error(errorMessage);
          throw error;
        } finally {
          Vue.prototype.$loading.stop();
        }
      },*/

      async create(url, data, options = {}) {
        if (options.confirm !== false) {
          const confirmed = await Vue.prototype.$confirm({
            title: "Confirmation",
            message: "Voulez-vous vraiment créer cet élément ?",
            confirmButtonText: "Oui",
            cancelButtonText: "Non",
          });

          if (!confirmed) return;
        }

        Vue.prototype.$loading.start();
        try {
          const requestOptions =
            options && !options.confirm
              ? { ...options, timeout: 30000 }
              : { timeout: 30000 };
          const response = await instance.post(url, data, requestOptions);

          if (response.data.status) {
            Vue.prototype.$toast.success(response.data.message);
          }

          return response;
        } catch (error) {
          console.error("API Create Error:", error);
          const errorMessage =
            error.response.data.message ||
            "Une erreur est survenue lors de la création.";
          Vue.prototype.$toast.error(errorMessage);
          throw error;
        } finally {
          Vue.prototype.$loading.stop();
        }
      },

      async update(url, data, options = {}) {
        // Show confirmation unless explicitly set to false
        if (options.confirm !== false) {
          const confirmed = await Vue.prototype.$confirm({
            title: "Confirmation",
            message: "Voulez-vous vraiment modifier cet élément ?",
            confirmButtonText: "Oui",
            cancelButtonText: "Non",
          });

          if (!confirmed) return;
        }

        Vue.prototype.$loading.start();
        try {
          const response = await instance.put(url, data, { timeout: 30000 });

          if (response.data.status) {
            Vue.prototype.$toast.success(response.data.message);
          }

          return response;
        } catch (error) {
          console.error("API Update Error:", error);
          const errorMessage =
            error.response.data.message ||
            "Une erreur est survenue lors de la mise à jour.";
          Vue.prototype.$toast.error(errorMessage);
          throw error;
        } finally {
          Vue.prototype.$loading.stop();
        }
      },

      async delete(url, options = {}) {
        if (!options.confirm) {
          const confirmed = await Vue.prototype.$confirm({
            title: "Confirmation",
            message: "Voulez-vous vraiment supprimer cet élément ?",
            confirmButtonText: "Oui",
            cancelButtonText: "Non",
          });

          if (!confirmed) return;
        }

        Vue.prototype.$loading.start();
        try {
          const response = await instance.delete(url, { timeout: 30000 });

          if (response.data.status) {
            Vue.prototype.$toast.success(response.data.message);
          }

          return response;
        } catch (error) {
          console.error("API Delete Error:", error);
          const errorMessage =
            error.response.data.message ||
            "Une erreur est survenue lors de la suppression.";
          Vue.prototype.$toast.error(errorMessage);
          throw error;
        } finally {
          Vue.prototype.$loading.stop();
        }
      },
    };
  },
};
